import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { useLinkStats, useLinkAnchors, type LinkStats, type AnchorTextItem } from '../hooks/useCrawls'

function StatCard({ label, value, variant }: { label: string; value: number; variant?: 'default' | 'destructive' | 'warning' }) {
  const color =
    variant === 'destructive' ? 'text-destructive' :
    variant === 'warning' ? 'text-yellow-600 dark:text-yellow-400' : ''
  return (
    <div className="text-center">
      <p className={`text-2xl font-bold tabular-nums ${color}`}>{value.toLocaleString()}</p>
      <p className="text-xs text-muted-foreground">{label}</p>
    </div>
  )
}

function StatsOverview({ stats }: { stats: LinkStats }) {
  return (
    <Card>
      <CardContent className="py-4">
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4 lg:grid-cols-8">
          <StatCard label="Total Links" value={stats.total} />
          <StatCard label="Internal" value={stats.internal} />
          <StatCard label="External" value={stats.external} />
          <StatCard label="Follow" value={stats.follow} />
          <StatCard label="Nofollow" value={stats.nofollow} variant={stats.nofollow > 0 ? 'warning' : 'default'} />
          <StatCard label="Broken" value={stats.broken} variant={stats.broken > 0 ? 'destructive' : 'default'} />
          <StatCard label="Empty Anchor" value={stats.emptyAnchor} variant={stats.emptyAnchor > 0 ? 'warning' : 'default'} />
          <StatCard label="Nofollow Int." value={stats.internalNofollow} variant={stats.internalNofollow > 0 ? 'destructive' : 'default'} />
        </div>
      </CardContent>
    </Card>
  )
}

type AnchorFilter = 'all' | 'internal' | 'external' | 'empty'

function AnchorTextTable({ anchors, totalCount, emptyCount }: { anchors: AnchorTextItem[]; totalCount: number; emptyCount: number }) {
  const [filter, setFilter] = useState<AnchorFilter>('all')

  const filtered = filter === 'empty'
    ? anchors.filter((a) => !a.anchor_text)
    : filter === 'all'
      ? anchors
      : anchors.filter((a) => a.link_type === filter)

  const truncated = totalCount > anchors.length

  const pills: { label: string; value: AnchorFilter }[] = [
    { label: 'All', value: 'all' },
    { label: 'Internal', value: 'internal' },
    { label: 'External', value: 'external' },
    { label: `Empty (${emptyCount})`, value: 'empty' },
  ]

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">
          Anchor Text Analysis
          {truncated && (
            <span className="ml-2 text-xs font-normal text-muted-foreground">
              top {anchors.length.toLocaleString()} of {totalCount.toLocaleString()} unique anchors by frequency
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex gap-1.5 mb-3 flex-wrap">
          {pills.map((p) => (
            <button
              key={p.value}
              onClick={() => setFilter(p.value)}
              className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium border transition-colors ${
                filter === p.value
                  ? 'bg-primary/10 border-primary text-primary'
                  : 'bg-muted/50 border-border text-muted-foreground hover:bg-accent'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>

        {filter === 'empty' ? (
          <div className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
            <p><span className="font-medium text-foreground">{emptyCount.toLocaleString()}</span> links have no anchor text (pure-image links, empty <code className="text-xs">&lt;a&gt;</code> tags, or icon-only links).</p>
            <p className="mt-2">Empty anchors aren't grouped by text (they all share "" as anchor). See <span className="font-medium text-foreground">Issues tab → Empty anchor text</span> for the per-page breakdown (source, target, count).</p>
          </div>
        ) : filtered.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">No matching anchor text found.</p>
        ) : (
          <div className="rounded-md border max-h-[500px] overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs">Anchor Text</TableHead>
                  <TableHead className="text-xs w-[80px]">Type</TableHead>
                  <TableHead className="text-xs w-[70px] text-right">Links</TableHead>
                  <TableHead className="text-xs w-[90px] text-right">Unique Sources</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((item, i) => (
                  <TableRow key={i}>
                    <TableCell className="text-xs max-w-[300px] truncate" title={item.anchor_text || '(empty)'}>
                      {item.anchor_text ? (
                        item.anchor_text
                      ) : (
                        <span className="text-muted-foreground italic">(empty)</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-[10px] px-1 py-0">
                        {item.link_type}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-right tabular-nums">{item.link_count}</TableCell>
                    <TableCell className="text-xs text-right tabular-nums">{item.source_pages}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
        {filter !== 'empty' && filtered.length > 0 && (
          <p className="text-xs text-muted-foreground mt-2">
            {filtered.length} unique anchor texts shown{filter !== 'all' ? ` · excludes ${emptyCount.toLocaleString()} empty-anchor links (see Empty pill)` : ''}
          </p>
        )}
      </CardContent>
    </Card>
  )
}

export function LinksTab({ crawlId }: { crawlId: string }) {
  const { data: stats, isLoading: statsLoading } = useLinkStats(crawlId)
  const { data: anchorData, isLoading: anchorsLoading } = useLinkAnchors(crawlId)

  if (statsLoading || anchorsLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-24" />
        <Skeleton className="h-64" />
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {stats && <StatsOverview stats={stats} />}
      {anchorData && <AnchorTextTable anchors={anchorData.anchors} totalCount={anchorData.totalAnchorCount} emptyCount={anchorData.emptyAnchorCount} />}
    </div>
  )
}
